import { useState } from 'react';
import { X } from 'lucide-react';
import { useSubmitFeedback } from '../lib/api/useFeedback';
import { useToast } from './ux/Toast';
import { useT } from '../lib/i18n';

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FeedbackDialog({ isOpen, onClose }: FeedbackDialogProps) {
  const { t } = useT();
  const [feedback, setFeedback] = useState('');
  const submitFeedback = useSubmitFeedback();
  const toast = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!feedback.trim()) {
      toast.error(t('feedback.errors.enterFeedback'));
      return;
    }

    try {
      await submitFeedback.mutateAsync(feedback);
      toast.success(t('feedback.thankYou'));
      setFeedback('');
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      toast.error(t('feedback.errors.submitFailed'));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-neutral-800 rounded-lg max-w-md w-full p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-dark-muted dark:text-neutral-400 hover:text-type-950 dark:hover:text-neutral-200"
          >
            <X className="w-5 h-5" />
          </button>

          <h2 className="text-2xl font-semibold mb-4 dark:text-neutral-100">{t('feedback.title')}</h2>
          <p className="text-dark-muted dark:text-neutral-400 mb-6">
            {t('feedback.description')}
          </p>

          <form onSubmit={handleSubmit}>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t('feedback.placeholder')}
              className="w-full border border-gray-300 dark:border-neutral-600 rounded-lg p-3 min-h-[150px] focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 bg-white dark:bg-neutral-700 text-gray-900 dark:text-neutral-100 placeholder-gray-400 dark:placeholder-neutral-500"
              maxLength={2000}
            />
            <div className="text-sm text-dark-muted dark:text-neutral-400 text-right mb-4">
              {feedback.length} / 2000
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 dark:text-neutral-200"
              >
                {t('common.buttons.cancel')}
              </button>
              <button
                type="submit"
                disabled={submitFeedback.isPending || !feedback.trim()}
                className="flex-1 btn-primary px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitFeedback.isPending ? t('feedback.submitting') : t('common.buttons.submit')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

